import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";
import { IoMdArrowDropdown } from "react-icons/io";
import { useTranslation } from "react-i18next";
import "./NavbarBrand.css";

// Memoized NavItem component for better performance
const NavItem = React.memo(({ to, label, ariaLabel, onClick }) => (
  <li className="nav-item">
    <NavLink
      to={to}
      className={({ isActive }) =>
        `nav-link-custom ${isActive ? "nav-link-active" : "nav-link-default"}`
      }
      aria-label={ariaLabel}
      onClick={onClick}
      prefetch="intent" // Preload on hover for faster navigation
    >
      {label}
    </NavLink>
  </li>
));

NavItem.displayName = 'NavItem';

// Memoized DropdownItem component
const DropdownItem = React.memo(({ to, children, onClick }) => (
  <MenuItem>
    <NavLink
      to={to}
      className="dropdown-item-custom"
      onClick={onClick}
      prefetch="intent"
    >
      {children}
    </NavLink>
  </MenuItem>
));

DropdownItem.displayName = 'DropdownItem';

// Debounce function for scroll optimization
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Memoized language change function
  const changeLanguage = useCallback((lang) => {
    i18n.changeLanguage(lang);
  }, [i18n]);

  // Debounced scroll handler for better performance
  const handleScroll = useMemo(
    () => debounce(() => {
      setIsScrolled(window.scrollY > 50);
    }, 10),
    []
  );

  // Handle navbar scroll effect with cleanup
  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  // Handle navigation with loading state
  const handleNavigation = useCallback((event) => {
    // Prevent multiple rapid clicks
    if (isNavigating) {
      event.preventDefault();
      return;
    }
    
    setIsNavigating(true);
    
    // Reset navigation state after a short delay
    setTimeout(() => {
      setIsNavigating(false);
    }, 500);
  }, [isNavigating]);

  // Memoized navigation items to prevent unnecessary re-renders
  const navigationItems = useMemo(() => [
    {
      to: "/",
      label: t("navbar.Home"),
      ariaLabel: "Home"
    },
    {
      to: "/trackapplication",
      label: t("navbar.Track Status"),
      ariaLabel: "Track Application Status"
    },
    {
      to: "/download",
      label: t("navbar.DownLoad"),
      ariaLabel: "Download Documents"
    }
  ], [t]);

  // Memoized dropdown items
  const dropdownItems = useMemo(() => [
    { to: "/super-admin", label: "DMC Officer" },
    { to: "/divisional-user", label: "Divisional Officer" },
    { to: "/nagar-department", label: "Nagar Rachana Officer" },
    { to: "/pwd-department", label: "MSEDCL Department" },
    { to: "/police-department", label: "Police Department" },
    { to: "/traffic-police", label: "Traffic Police Department" },
    { to: "/fire-department", label: "Fire Department" }
  ], []);

  return (
    <div className="navbar-container">
      {/* Top Bar with Social Links and Language Selector */}
      <div className="top-bar">
        <div className="container-fluid">
          <div className="row gx-0 align-items-center">
            <div className="col-md-6 text-center text-lg-start">
              <div className="social-icons d-inline-flex align-items-center">
                {/* <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Twitter"
                >
                  <i className="fab fa-twitter"></i>
                </a> */}
                <a
                  href="https://www.facebook.com/ahmednagar.prasiddhi.7/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                >
                  <i className="fab fa-facebook"></i>
                </a>
                <a
                  href="https://www.instagram.com/amcnagar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.youtube.com/@ahilyanagarMC"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Subscribe to our YouTube channel"
                >
                  <i className="fab fa-youtube"></i>
                </a>
              </div>
            </div>
            <div className="col-md-6 text-center text-lg-end">
              <div className="d-inline-flex align-items-center">
                <select
                  name="language"
                  id="language-select"
                  onChange={(event) => changeLanguage(event.target.value)}
                  className="language-selector"
                  aria-label="Select Language"
                >
                  <option value="mr">मराठी</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`navbar navbar-expand-lg main-navbar ${
          isScrolled ? "scrolled" : ""
        } ${isNavigating ? "navigating" : ""}`}
      >
        <div className="container-fluid">
          {/* Logo and Title Section */}
          <div className="logo-container">
            <img
              src="/logo1.jpeg"
              alt="Ahilyanagar Municipal Corporation Logo"
              className="navbar-logo"
              loading="lazy"
            />
            <div className="corp-title">
              <h5>Ahilyanagar Municipal Corporation, Ahilyanagar</h5>
              <h6>अहिल्यानगर महानगर पालिका, अहिल्यानगर</h6>
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Navigation Menu */}
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav ms-auto mb-2 mb-lg-0 d-flex align-items-center gap-3">
              {navigationItems.map((item) => (
                <NavItem
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  ariaLabel={item.ariaLabel}
                  onClick={handleNavigation}
                />
              ))}

              {/* Login Dropdown */}
              <li className="nav-item">
                <Dropdown>
                  <MenuButton className="login-dropdown-btn">
                    {t("navbar.Login")} <IoMdArrowDropdown />
                  </MenuButton>
                  <Menu className="dropdown-menu-custom">
                    {dropdownItems.map((item) => (
                      <DropdownItem
                        key={item.to}
                        to={item.to}
                        onClick={handleNavigation}
                      >
                        {item.label}
                      </DropdownItem>
                    ))}
                  </Menu>
                </Dropdown>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Loading overlay for navigation */}
      {isNavigating && (
        <div className="navigation-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Navbar);